/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

/** Names the refusal for a request that was never sent because this browser holds no usable sign-in. */
export const SESSION_EXPIRED_CODE = 'LAC_SessionExpired';

/** Why a store request was turned away, as much of it as the thrown error carried. */
export interface IStoreRequestFailure {
    IsSuccess: false;
    ErrorMessage?: string;
    ErrorCode?: string;
}

interface IThrownErrorFields {
    ErrorMessage?: unknown;
    ErrorResourceId?: unknown;
    ErrorCode?: unknown;
}

interface IThrownActionError {
    data?: IThrownErrorFields;
    error?: { data?: IThrownErrorFields };
    message?: unknown;
}

/**
 * Reads a thrown data action error as the refusal it stands for, so a request that failed by throwing is
 * answered the same way as one that came back saying it had been refused. A data action passes the proxy's
 * error on inside one of its own, so the store's own fields sit a level down whenever that wrapping
 * happened.
 */
export const readStoreRequestFailure = (error: unknown): IStoreRequestFailure => {
    const actionError = error as IThrownActionError | undefined;
    const data = actionError?.data ?? actionError?.error?.data;
    const reported: unknown = data?.ErrorMessage ?? actionError?.message;
    const named: unknown = data?.ErrorCode ?? data?.ErrorResourceId;

    return {
        IsSuccess: false,
        ErrorMessage: typeof reported === 'string' ? reported : undefined,
        ErrorCode: typeof named === 'string' ? named : undefined
    };
};

export const storeRequestSessionExpired = (): IStoreRequestFailure => ({ IsSuccess: false, ErrorCode: SESSION_EXPIRED_CODE });
